import {EMBEDDED_MENU_PORTAL_CLASS, MenuPortalContainer} from '@github-ui/copilot-chat/components/PortalContainerUtils'
import {useEntitlement} from '@github-ui/copilot-chat/components/quota/EntitlementContext'
import {TraceDisplay} from '@github-ui/copilot-chat/components/tracing/TraceDisplay'
import {useTraceState} from '@github-ui/copilot-chat/components/tracing/TraceProvider'
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {useSelectedCopilotSpaceId} from '@github-ui/copilot-chat/hooks/use-selected-custom-copilot-id'
import type {CopilotSpaceId} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {CopilotLocalStorage, copilotLocalStorage} from '@github-ui/copilot-chat/utils/copilot-local-storage'
import {useChatStateValues} from '@github-ui/copilot-chat/utils/CopilotChatContext'
import {useFetchCopilotSpace} from '@github-ui/copilot-spaces/hooks'
import {useSpaceSelection} from '@github-ui/copilot-spaces/hooks/use-space-selection'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import type {AnchorSide} from '@primer/behaviors'
import {clsx} from 'clsx'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {useHandleMessageSubmit} from '../hooks/use-handle-message-submit'
import {useIsSharedThread} from '../hooks/use-is-shared-thread'
import {ChatInputContainer} from './ChatInputContainer'
import styles from './ConversationView.module.css'
import {ErrorFallback} from './ErrorFallback'
import {ImmersiveChat} from './ImmersiveChat'
import {NewConversation} from './NewConversation/NewConversation'
import {NotificationsSection} from './NotificationsSection'
import {spaceConversationVariants, useSpaceConversationStatus} from './Spaces/SpaceConversationBanner'

interface ConversationViewProps {
  isMobile: boolean
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>
  attachmentMenuSide?: AnchorSide
  /** Force icon-only mode in the toolbar, useful for container-based responsive behavior */
  forceIconOnly?: boolean
  /** Whether this is the embedded immersive view (affects which suggestions are shown) */
  isEmbedded?: boolean
}

function ConversationViewInner({
  isMobile,
  textAreaRef,
  attachmentMenuSide,
  forceIconOnly,
  isEmbedded,
}: ConversationViewProps) {
  const state = useChatStateValues(
    'messages',
    'messagesLoading',
    'threadsLoading',
    'selectedThreadID',
    'currentTopic',
    'pendingFirstMessage',
    'shouldReplaceHistoryOnNavigation',
    'currentReferences',
    'sharedThreadChannel',
    'mode',
  )
  const manager = useChatManager()
  const traceState = useTraceState()
  const {messages, messagesLoading, threadsLoading} = state
  const {chatQuotaExceeded} = useEntitlement()
  const customCopilotId = useSelectedCopilotSpaceId()
  const {data: customCopilot} = useFetchCopilotSpace(customCopilotId)
  const customCopilotExists = Boolean(customCopilot)
  const {variant: spaceViewVariant} = useSpaceConversationStatus(customCopilotId, state.selectedThreadID)
  const spaceSizeExceeded = spaceViewVariant === spaceConversationVariants.customCopilotSizeExceeded
  const showChatInput = (!spaceViewVariant || customCopilotExists) && !spaceSizeExceeded
  const {handleSpaceSelect} = useSpaceSelection()

  const currentTopicRef = useRef(state.currentTopic)
  const isSharedThread = useIsSharedThread()
  const hasEntrypointMessage = useMemo(
    () => !state.selectedThreadID && copilotLocalStorage.getEntrypointMessage(),
    [state.selectedThreadID],
  )
  const shouldShowNewConversation = useMemo(() => {
    if (
      state.selectedThreadID || // If there's a selected thread, we know it's not a new conversation
      state.pendingFirstMessage || // If the user sent a message but the thread hasn't been created yet, show the ImmersiveChat
      hasEntrypointMessage || // If there's an entrypoint message, kick off chat right away
      customCopilotId || // Custom copilots are currently still handled in the ImmersiveChat, should probably be moved up to Layout
      messagesLoading.state === 'error' || // Don't show if there's an error state because we do show the input at the bottom then
      threadsLoading.state === 'error' ||
      state.shouldReplaceHistoryOnNavigation // Don't show if we're about to reroute to a thread.
    ) {
      return false
    }

    return true
  }, [
    state.selectedThreadID,
    state.pendingFirstMessage,
    hasEntrypointMessage,
    customCopilotId,
    messagesLoading.state,
    threadsLoading.state,
    state.shouldReplaceHistoryOnNavigation,
  ])

  const nextMessageIndex = messages.length

  const handleUserSubmit = useHandleMessageSubmit(currentTopicRef, nextMessageIndex)

  useEffect(() => {
    currentTopicRef.current = state.currentTopic
  }, [state.currentTopic])

  useEffect(() => {
    const sendMessage = async (message: NonNullable<ReturnType<CopilotLocalStorage['getEntrypointMessage']>>) => {
      copilotLocalStorage.clearEntrypointMessage(state.selectedThreadID)

      // If the message has a space reference, select that space before submitting the message
      const space = message?.references?.find?.(ref => ref.type === 'copilot-space')
      const copilotSpaceId: CopilotSpaceId | null = space ? {id: space.id, owner: space.owner} : null
      if (copilotSpaceId) handleSpaceSelect(copilotSpaceId)

      if (message.model) {
        manager.selectModel(message.model)
      }

      if (!state.selectedThreadID) {
        // Set flag to replace history on navigation since this is from an entrypoint
        manager.setShouldReplaceHistoryOnNavigation(true)
      }

      let thread = state.selectedThreadID ? manager.getThreadById(state.selectedThreadID) : null
      if (state.selectedThreadID && !thread && state.threadsLoading.state !== 'loaded') {
        // if the threads haven't loaded yet, wait for them to load so we send to the correct thread
        const threads = await manager.fetchThreads()
        thread = threads?.find(t => t.id === state.selectedThreadID) || null
      }

      // If the thread has no messages, this is the first message of a new thread originated
      // from the dashboard entrypoint. The dashboard flow uses noSend:true to pre-create the
      // thread and then navigates here, so the normal !thread guard in sendChatMessage never
      // runs on this manager instance. Mark it so first_message_rendered fires on completion.
      if (messages.length === 0) {
        manager.markFirstMessageInNewThread()
      }

      // eslint-disable-next-line @eslint-react/web-api/no-leaked-timeout
      setTimeout(() => {
        void handleUserSubmit(
          message.message,
          message.references,
          message.model,
          copilotSpaceId, // Pass copilotSpaceId explicitly because React state updates (from handleSpaceSelect above) are asynchronous and may not be reflected in activeCopilotSpaceId yet. This ensures the message is sent with the correct space.
          message.mediaContent,
          thread,
        )
      }, 100)
    }

    const message = copilotLocalStorage.getEntrypointMessage(state.selectedThreadID)
    if (message && !chatQuotaExceeded) {
      void sendMessage(message)
    }
  }, [
    handleUserSubmit,
    chatQuotaExceeded,
    state.currentReferences,
    manager,
    handleSpaceSelect,
    state.selectedThreadID,
    state.threadsLoading.state,
    messages.length,
  ])

  const content = (
    <div className={clsx(styles.root, {[styles.embeddedRoot]: isEmbedded})}>
      {traceState.traceInfo?.visible ? (
        <TraceDisplay sharedThreadChannel={state.sharedThreadChannel} threadId={state.selectedThreadID} />
      ) : (
        <>
          <div className={styles.container}>
            {shouldShowNewConversation ? (
              <NewConversation
                textAreaRef={textAreaRef}
                handleUserSubmit={handleUserSubmit}
                nextMessageIndex={nextMessageIndex}
                forceIconOnly={forceIconOnly}
                isEmbedded={isEmbedded}
              />
            ) : (
              <ImmersiveChat inputRef={textAreaRef} isMobile={isMobile} />
            )}
          </div>
          {(!isSharedThread || copilotFeatureFlags.copilotDuplicateThread) && !shouldShowNewConversation && (
            <div className={styles.footer}>
              {state.mode === 'assistive' && <MenuPortalContainer className={EMBEDDED_MENU_PORTAL_CLASS} />}
              {!showChatInput && <NotificationsSection />}
              {showChatInput && (
                <ChatInputContainer
                  textAreaRef={textAreaRef}
                  handleUserSubmit={handleUserSubmit}
                  nextMessageIndex={nextMessageIndex}
                  slashCommandsEnabled
                  isNewConversation={false}
                  attachmentMenuSide={attachmentMenuSide}
                  forceIconOnly={forceIconOnly}
                />
              )}
            </div>
          )}
          {isEmbedded && shouldShowNewConversation && (
            <div className={styles.footer}>
              {state.mode === 'assistive' && <MenuPortalContainer className={EMBEDDED_MENU_PORTAL_CLASS} />}
              <ChatInputContainer
                textAreaRef={textAreaRef}
                handleUserSubmit={handleUserSubmit}
                nextMessageIndex={nextMessageIndex}
                slashCommandsEnabled
                isNewConversation
                showDisabledInputOnQuotaExceeded
                attachmentMenuSide={attachmentMenuSide}
                forceIconOnly={forceIconOnly}
              />
            </div>
          )}
        </>
      )}
    </div>
  )

  return content
}

export function ConversationView(props: ConversationViewProps) {
  return (
    <ConversationViewErrorBoundary>
      <ConversationViewInner {...props} />
    </ConversationViewErrorBoundary>
  )
}

export function ConversationViewErrorBoundary(props: {children: React.ReactNode}) {
  const {selectedThreadID} = useChatStateValues('selectedThreadID')
  const [key, setKey] = useState(0)
  const hasErrored = useRef(false)
  const onError = useCallback(() => {
    hasErrored.current = true
  }, [])

  // If we had an error, and then change threads, change the key to reset the error state.
  useEffect(() => {
    if (hasErrored.current) {
      setKey(prev => prev + 1)
      hasErrored.current = false
    }
  }, [selectedThreadID])

  return (
    <ErrorBoundary key={key} fallback={<ErrorFallback regionName="This conversation" />} onError={onError}>
      {props.children}
    </ErrorBoundary>
  )
}
